import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, MapPin, CreditCard, Package } from "lucide-react";
import { api, inr, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { statusChip, statusLabel } from "./OrdersPage";

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (user === false) return nav("/auth", { state: { from: `/orders/${orderId}` } });
    if (user) {
      api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => setMissing(true));
    }
  }, [user, orderId, nav]);

  if (missing) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-not-found">
        <h1 className="font-serif text-3xl font-bold text-stone-900">We couldn't find this order</h1>
        <p className="text-stone-500 mt-3">Double-check the order ID, or visit your orders page.</p>
        <Link to="/orders" className="inline-block mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold">My Orders</Link>
      </div>
    );
  }

  if (!order) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-stone-400 text-sm">Loading order…</div>;

  const currentIdx = order.status_steps.findIndex((s) => s.key === order.status);
  const a = order.address;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 lg:py-14" data-testid="order-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs text-stone-400 uppercase tracking-wider font-bold">Order</p>
          <h1 className="font-mono text-2xl font-bold text-stone-900" data-testid="order-detail-id">{order.order_id}</h1>
          <p className="text-sm text-stone-400 mt-1">Placed on {fmtDate(order.created_at)}</p>
        </div>
        <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${statusChip(order.status)}`} data-testid="order-status-chip">{statusLabel(order.status)}</span>
      </div>

      {/* TIMELINE */}
      <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-white border border-[#E9E2D7]" data-testid="order-timeline">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 mb-6">Delivery journey · ETA {fmtDate(order.estimated_delivery)}</p>
        <div className="flex items-start">
          {order.status_steps.map((s, i) => {
            const done = i <= currentIdx;
            const hist = order.status_history.find((h) => h.status === s.key);
            return (
              <div key={s.key} className="flex-1 relative" data-testid={`timeline-${s.key}`}>
                {i > 0 && <div className={`absolute top-4 -left-1/2 w-full h-0.5 ${done ? "bg-[#78866B]" : "bg-stone-200"}`} />}
                <div className="relative flex flex-col items-center text-center">
                  <span className={`w-8 h-8 rounded-full grid place-items-center border-2 transition-colors ${done ? "bg-[#78866B] border-[#78866B] text-white" : "bg-white border-stone-200 text-stone-300"}`}>
                    <Check size={14} />
                  </span>
                  <p className={`text-[10px] sm:text-xs font-semibold mt-2 ${done ? "text-stone-800" : "text-stone-400"}`}>{s.label}</p>
                  {hist && <p className="text-[10px] text-stone-400 mt-0.5 hidden sm:block">{fmtDate(hist.at)}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><Package size={13} /> Gifts in this order</p>
          <div className="mt-4 space-y-4">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3">
                {i.image && <img src={i.image} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${i.product_id}`} className="text-sm font-semibold text-stone-800 hover:text-[#B45309] line-clamp-1">{i.name}</Link>
                  <p className="text-xs text-stone-400">Qty {i.qty}{i.personalization?.recipient_name ? ` · For ${i.personalization.recipient_name}` : ""}</p>
                  {i.personalization?.photo_data && <img src={i.personalization.photo_data} alt="Personalization upload" className="w-9 h-9 rounded-lg object-cover border border-stone-200 mt-1" />}
                  {order.status === "delivered" && <Link to={`/product/${i.product_id}`} className="text-xs font-bold text-[#B45309] hover:underline" data-testid={`review-link-${i.product_id}`}>Write a review →</Link>}
                </div>
                <span className="text-sm font-bold text-stone-900">{inr(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 mt-4 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-stone-500"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
            {order.coupon_discount > 0 && <div className="flex justify-between text-[#5c6b52] font-semibold"><span>Coupon {order.coupon_code}</span><span>−{inr(order.coupon_discount)}</span></div>}
            <div className="flex justify-between text-stone-500"><span>Delivery</span><span>{order.delivery_charge === 0 ? "FREE" : inr(order.delivery_charge)}</span></div>
            <div className="flex justify-between font-bold text-stone-900 text-base pt-1"><span>Total</span><span data-testid="order-detail-total">{inr(order.total)}</span></div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><MapPin size={13} /> Delivering to</p>
            <p className="font-bold text-stone-800 text-sm mt-3">{a.full_name} · {a.mobile}</p>
            <p className="text-sm text-stone-500 mt-1">{a.house}, {a.street}, {a.area}, {a.city}, {a.state} — {a.pincode}</p>
          </div>
          <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 flex items-center gap-2"><CreditCard size={13} /> Payment</p>
            <p className="text-sm font-semibold text-stone-800 mt-3">{order.payment_method.toUpperCase()} · <span className={order.payment_status === "paid" ? "text-[#5c6b52]" : "text-amber-700"}>{order.payment_status === "paid" ? "Paid" : "Pay on delivery"}</span></p>
            <p className="text-xs text-stone-400 mt-1">Test-mode payment · {order.delivery_type.replace("_", " ")} delivery</p>
          </div>
          {order.gift_note && (
            <div className="p-6 rounded-2xl bg-[#F9F3EA] border border-[#E0A96D]/40">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#B45309]">Gift note</p>
              <p className="font-serif italic text-stone-700 mt-2">"{order.gift_note}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
